import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Modal, Button, Form, Input, Select, Radio, message, Row, Col, } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import useFetchApiData from "../helper/useFetchApiData";
import { Link } from "react-router-dom";
 
const QuantityControl = ({ initialQuantity, onIncrease, onDecrease }) => {
  return (
    <div className="flex items-center">
      <button
        type="button"
        onClick={onDecrease}
        className="border px-2 py-1 rounded-l"
      >
        -
      </button>
      <input
        type="number"
        value={initialQuantity}
        readOnly
        className="w-12 text-center border-y py-1"
      />
      <button
        type="button"
        onClick={onIncrease}
        className="border px-2 py-1 rounded-r"
      >
        +
      </button>
    </div>
  );
};

QuantityControl.propTypes = {
  initialQuantity: PropTypes.number.isRequired,
  onIncrease: PropTypes.func.isRequired,
  onDecrease: PropTypes.func.isRequired,
};

const CartSection = () => {
  const [visible, setVisible] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grandTotal, setGrandTotal] = useState(0);
  const [editableQuantities, setEditableQuantities] = useState({});
  const [orderAmount, setOrderAmount] = useState(500);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { Option } = Select;

  // Fetch Address Data
  const addresses = useFetchApiData("/customer/address/list");
  console.warn("Address", addresses);

  // Memoized cart fetch
  useEffect(() => {
    const fetchCartData = async () => {
      try {
        const cart = JSON.parse(localStorage.getItem("cart") || "[]");
        const cartData = await Promise.all(
          cart.map(async (item) => {
            try {
              const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URI}/products/details/${item.id}`
              );
              const product = response.data.product;
              return {
                id: product.id,
                name: product.name,
                image: product.image_fullpath[0],
                price: product.price,
                quantity: item.qty,
                subtotal: product.price * item.qty,
              };
            } catch (productError) {
              console.error(`Error fetching product ${item.id}:`, productError);
              return null;
            }
          })
        );

        const validCartData = cartData.filter((item) => item !== null);

        setCartItems(validCartData);

        const initialEditableQuantities = validCartData.reduce((acc, item) => {
          acc[item.id] = item.quantity;
          return acc;
        }, {});

        setEditableQuantities(initialEditableQuantities);
        setGrandTotal(calculateGrandTotal(validCartData));
        setLoading(false);
      } catch (error) {
        console.error("Error fetching cart data:", error);
        setLoading(false);
        message.error("Failed to load cart items");
      }
    };

    fetchCartData();
  }, []);

  // Utility Functions
  const calculateGrandTotal = (items) => {
    return items.reduce((total, item) => total + item.subtotal, 0);
  };

  const updateLocalStorage = (updatedCart) => {
    const storageCart = updatedCart.map((item) => ({
      id: item.id,
      qty: item.quantity,
    }));
    localStorage.setItem("cart", JSON.stringify(storageCart));
  };

  // Handlers
  const handleQuantityChange = (id, value) => {
    setEditableQuantities((prev) => ({
      ...prev,
      [id]: value === "" ? 1 : Math.max(1, parseInt(value, 10)),
    }));
  };

  const updateQuantity = (id) => {
    setCartItems((prevItems) => {
      const updatedItems = prevItems.map((item) => {
        if (item.id === id) {
          const newQuantity = editableQuantities[id];
          return {
            ...item,
            quantity: newQuantity,
            subtotal: item.price * newQuantity,
          };
        }
        return item;
      });

      updateLocalStorage(updatedItems);
      setGrandTotal(calculateGrandTotal(updatedItems));

      return updatedItems;
    });
  };

  const removeItem = (id) => {
    const updatedCartItems = cartItems.filter((cartItem) => cartItem.id !== id);

    setCartItems(updatedCartItems);
    updateLocalStorage(updatedCartItems);
    setGrandTotal(calculateGrandTotal(updatedCartItems));

    setEditableQuantities((prev) => {
      const newQuantities = { ...prev };
      delete newQuantities[id];
      return newQuantities;
    });
  };

  const handleApplyCoupon = () => {
    const couponCode = formik.values.coupon_code;

    if (!couponCode) {
      message.error("Please enter a coupon code");
      return;
    }

    // Loading state for coupon application
    setLoading(true);

    axios
      .get(
        `${process.env.REACT_APP_BACKEND_URI}/coupon/apply?code=${couponCode}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      )
      .then((response) => {
        const couponData = response.data;

        // Validate coupon eligibility
        const currentDate = new Date();
        const startDate = new Date(couponData.start_date);
        const expireDate = new Date(couponData.expire_date);

        // Check coupon status and dates
        if (couponData.status !== 1) {
          message.error("This coupon is currently inactive");
          return;
        }

        if (currentDate < startDate || currentDate > expireDate) {
          message.error("Coupon is not valid at this time");
          return;
        }

        // Check minimum purchase requirement
        if (grandTotal < couponData.min_purchase) {
          message.error(
            `Minimum purchase of Rs. ${couponData.min_purchase} required`
          );
          return;
        }

        // Calculate discount
        let discountAmount = 0;
        if (couponData.discount_type === "percent") {
          discountAmount = Math.min(
            (grandTotal * couponData.discount) / 100,
            couponData.max_discount
          );
        } else if (couponData.discount_type === "flat") {
          discountAmount = couponData.discount;
        }

        // Apply discount
        setGrandTotal(grandTotal - discountAmount);

        // Update formik values with coupon details
        formik.setFieldValue("coupon_discount_amount", discountAmount);
        formik.setFieldValue("coupon_discount_title", couponData.title);
        formik.setFieldValue("coupon_code", couponData.code);

        message.success(
          `Coupon applied! You saved Rs. ${discountAmount.toFixed(2)}`
        );
      })
      .catch((error) => {
        // Handle different error scenarios
        if (error.response) {
          // The request was made and the server responded with a status code
          switch (error.response.status) {
            case 404:
              message.error("Coupon not found");
              break;
            case 400:
              message.error("Invalid coupon");
              break;
            default:
              message.error("Failed to apply coupon");
          }
        } else if (error.request) {
          // The request was made but no response was received
          message.error("No response from server. Please try again.");
        } else {
          // Something happened in setting up the request
          message.error("Error applying coupon");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Formik Configuration
  const formik = useFormik({
    initialValues: {
      order_amount: 500,
      delivery_address_id: "",
      order_type: "delivery",
      branch_id: 1,
      cart: cartItems,
      coupon_discount_amount: 50,
      coupon_discount_title: "Summer Sale",
      coupon_code: "",
      payment_method: "cash_on_delivery",
      order_note: "",
      distance: 10,
      selected_delivery_area: 5,
      user_token: localStorage.getItem("user_token") || "",
    },
    validationSchema: Yup.object({
      delivery_address_id: Yup.string().required(
        "Delivery address is required"
      ),
      order_type: Yup.string().required("Order type is required"),
      payment_method: Yup.string().required("Payment method is required"),
      order_note: Yup.string()
        .max(500, "Order note must be at most 500 characters")
        .optional(),
    }),
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        // Implement actual order submission logic here
        console.log(values);
        message.success("Order submitted successfully!");
        setVisible(false);
      } catch (error) {
        message.error("Failed to submit order");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Render Methods
  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <section className="cart py-80">
      {/* Cart Items Table */}
      <div className="container container-lg">
        <div className="row gy-4">
          <div className="col-xl-9 col-lg-8">
            <div className="cart-table border border-gray-100 rounded-8 px-40 py-48">
              <table className="table w-full">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Product Name</th>
                    <th>Price</th>
                    <th>Quantity</th>
                    <th>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {cartItems.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="text-red-500 hover:text-red-700 btn btn-danger"
                        >
                          Remove
                        </button>
                      </td>
                      <td>
                        <div className="flex">
                          <img
                            src={item.image}
                            alt={item.name}
                            style={{ width: "80px" }}
                          />
                          {"  "}
                          <span>{item.name}</span>
                        </div>
                      </td>
                      <td>Rs. {item.price}</td>
                      <td>
                        <QuantityControl
                          style={{ width: "100%" }}
                          initialQuantity={editableQuantities[item.id]}
                          onIncrease={() =>
                            handleQuantityChange(
                              item.id,
                              (editableQuantities[item.id] || 1) + 1
                            )
                          }
                          onDecrease={() =>
                            handleQuantityChange(
                              item.id,
                              Math.max(
                                1,
                                (editableQuantities[item.id] || 1) - 1
                              )
                            )
                          }
                        />
                        <button
                          onClick={() => updateQuantity(item.id)}
                          className="btn btn-primary btn-sm"
                        >
                          Update
                        </button>
                      </td>
                      <td>Rs. {item.subtotal.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cart Sidebar */}
          <div className="col-xl-3 col-lg-4">
            <div className="cart-sidebar border rounded-8 p-20">
              <h6 className="text-xl mb-4">Cart Totals</h6>
              <div className="bg-gray-100 rounded-8 p-4">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>Rs. {grandTotal.toFixed(2)}</span>
                </div>
              </div>
              <Link
                to="/checkout"
                className="btn btn-primary mt-4 w-full"
                onClick={() => setVisible(true)}
              >
                Proceed to Checkout
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Checkout Modal */}
       
    </section>
  );
};

export default CartSection;
